import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Loader2, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { compressImageFile, approxBase64Bytes } from "../utils/imageCompress";

/**
 * Standalone P2P KYC — verify once, trade many times.
 * India users only. Lets users pre-verify before initiating any trade.
 */
const P2PStandaloneKycPage = () => {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const api = authAxios();

  const [status, setStatus] = useState(null);
  const [aadharFront, setAadharFront] = useState("");
  const [aadharBack, setAadharBack] = useState("");
  const [selfie, setSelfie] = useState("");
  const [phone, setPhone] = useState("");
  const [bvn, setBvn] = useState("");
  const [nin, setNin] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [compressing, setCompressing] = useState(null);

  useEffect(() => {
    api.get("/p2p/me/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const isNigeria = status?.country === "nigeria";
  const isIndia = status?.country === "india";

  const handleFile = (setter, label) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCompressing(label);
    try {
      const originalKb = Math.round(f.size / 1024);
      const dataUrl = await compressImageFile(f, { targetMaxBytes: 110 * 1024, maxWidth: 1024, maxHeight: 1024, initialQuality: 0.7, minQuality: 0.3 });
      const finalKb = Math.round(approxBase64Bytes(dataUrl) / 1024);
      setter(dataUrl);
      toast.success(`${label}: ${originalKb} KB → ${finalKb} KB`);
    } catch (err) {
      toast.error("Image compress failed: " + err.message);
    }
    setCompressing(null);
  };

  const submit = async () => {
    if (!aadharFront || !aadharBack || !selfie || !phone) {
      toast.error("All fields are required");
      return;
    }
    if (isNigeria) {
      if (!/^\d{11}$/.test(bvn)) { toast.error("BVN must be 11 digits"); return; }
      if (!/^\d{11}$/.test(nin)) { toast.error("NIN must be 11 digits"); return; }
      if (!/^\d{10,14}$/.test(phone)) { toast.error("Enter a valid Nigerian phone (e.g. 0801…)"); return; }
    } else {
      if (!/^\d{10}$/.test(phone)) { toast.error("Phone must be 10 digits"); return; }
    }
    setBusy(true);
    try {
      const r = await api.post(`/p2p/kyc/standalone`, {
        aadhar_front_b64: aadharFront,
        aadhar_back_b64: aadharBack,
        selfie_b64: selfie,
        phone,
        bvn_number: isNigeria ? bvn : undefined,
        nin_number: isNigeria ? nin : undefined,
      });
      if (r.data.status === "already_verified") {
        toast.success("Already verified!");
        navigate("/p2p");
        return;
      }
      toast.success("KYC submitted for verification. You'll be notified within 5–30 minutes.");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    }
    setBusy(false);
  };

  if (status?.country !== "india" && status?.country !== "nigeria") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center text-muted-foreground text-sm">
          Standalone KYC is required only for India / Nigeria users.
          <button onClick={() => navigate("/p2p")} className="block mt-4 text-primary underline">Back to P2P</button>
        </div>
      </div>
    );
  }

  if (status?.kyc_verified || submitted) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 py-3">
          <button onClick={() => navigate("/p2p")} className="p-1 mr-2"><ChevronLeft size={24} /></button>
          <h1 className="text-lg font-bold">KYC Status</h1>
        </div>
        <div className="px-4 py-8 text-center space-y-4">
          {status?.kyc_verified ? (
            <>
              <CheckCircle2 size={64} className="mx-auto text-green-400" />
              <div className="text-lg font-bold text-green-400">KYC Verified ✓</div>
              <div className="text-sm text-muted-foreground">Aap ab kabhi bhi P2P par trade kar sakte hain. Future trades me bank details turant mil jayengi.</div>
              <button onClick={() => navigate("/p2p")} className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold">
                Start Trading
              </button>
            </>
          ) : (
            <>
              <Loader2 size={64} className="mx-auto animate-spin text-yellow-400" />
              <div className="text-lg font-bold text-yellow-400">Under Verification</div>
              <div className="text-sm text-muted-foreground">Submitted. 5–30 min me verify ho jayega. Approve hone ke baad notification milegi.</div>
              <button onClick={() => navigate("/p2p")} className="px-6 py-2 border border-border rounded-lg text-sm">
                Back to P2P
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1 mr-2" data-testid="standalone-kyc-back">
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold flex items-center gap-2">
          <ShieldCheck size={20} className="text-primary" /> Complete P2P KYC
        </h1>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-xs flex gap-2">
          <AlertCircle size={16} className="text-blue-400 shrink-0 mt-0.5" />
          <div>
            Complete KYC in advance to avoid waiting on every trade. Once verified, the seller's bank account is revealed instantly.
          </div>
        </div>

        <FileBlock
          label={isNigeria ? "BVN Slip / Government ID — Front" : "Aadhar Front Side"}
          value={aadharFront}
          onChange={handleFile(setAadharFront, isNigeria ? "BVN Slip Front" : "Aadhar Front")}
          testId="standalone-aadhar-front"
          loading={compressing === (isNigeria ? "BVN Slip Front" : "Aadhar Front")}
        />
        <FileBlock
          label={isNigeria ? "NIN Slip / Government ID — Back" : "Aadhar Back Side"}
          value={aadharBack}
          onChange={handleFile(setAadharBack, isNigeria ? "NIN Slip" : "Aadhar Back")}
          testId="standalone-aadhar-back"
          loading={compressing === (isNigeria ? "NIN Slip" : "Aadhar Back")}
        />
        <FileBlock
          label={isNigeria ? "Selfie holding your BVN/NIN slip" : "Selfie with Aadhar in hand"}
          value={selfie}
          onChange={handleFile(setSelfie, "Selfie")}
          testId="standalone-selfie"
          loading={compressing === "Selfie"}
        />

        {isNigeria && (
          <>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">BVN (11 digits)</label>
              <input
                value={bvn}
                onChange={(e) => setBvn(e.target.value.replace(/\D/g, "").slice(0, 11))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                placeholder="22XXXXXXXXX"
                data-testid="standalone-kyc-bvn"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">NIN (11 digits)</label>
              <input
                value={nin}
                onChange={(e) => setNin(e.target.value.replace(/\D/g, "").slice(0, 11))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                placeholder="12345678901"
                data-testid="standalone-kyc-nin"
              />
            </div>
          </>
        )}

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{isNigeria ? "Phone Number" : "Phone Number (10 digits)"}</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, isNigeria ? 14 : 10))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
            placeholder={isNigeria ? "0801XXXXXXX" : "9XXXXXXXXX"}
            data-testid="standalone-kyc-phone"
          />
        </div>

        <button
          onClick={submit}
          disabled={busy || compressing !== null}
          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-bold disabled:opacity-50"
          data-testid="standalone-kyc-submit"
        >
          {busy ? <Loader2 className="animate-spin inline" /> : "Submit for Verification"}
        </button>
      </div>
    </div>
  );
};

const FileBlock = ({ label, value, onChange, testId, loading }) => (
  <div className="bg-card border border-border rounded-xl p-3 space-y-2 relative">
    <label className="text-xs text-muted-foreground block">{label}</label>
    <input type="file" accept="image/*" onChange={onChange} disabled={loading} className="text-xs w-full" data-testid={testId} />
    {loading && <CompressionOverlay label={label} />}
    {value && !loading && <img src={value} alt={label} className="rounded-lg max-h-40 mx-auto" />}
  </div>
);

const CompressionOverlay = ({ label }) => {
  const [dots, setDots] = React.useState(1);
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    const dotIv = setInterval(() => setDots((d) => (d % 3) + 1), 350);
    const timeIv = setInterval(() => setElapsed((s) => s + 0.1), 100);
    return () => { clearInterval(dotIv); clearInterval(timeIv); };
  }, []);
  return (
    <div className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center gap-2 z-10" data-testid="compression-overlay">
      <div className="relative">
        <div className="w-16 h-16 rounded-full border-4 border-yellow-500/30 border-t-yellow-400 animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-yellow-400">
          {elapsed.toFixed(1)}s
        </div>
      </div>
      <div className="text-sm font-semibold text-yellow-400">
        Compressing {label}{".".repeat(dots)}
      </div>
      <div className="text-[10px] text-muted-foreground text-center px-2">
        Reducing size to ~100 KB for faster upload<br />
        <span className="text-yellow-500/80">Please wait, do not close...</span>
      </div>
      <div className="flex gap-1 mt-1">
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></span>
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}></span>
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}></span>
      </div>
    </div>
  );
};

export default P2PStandaloneKycPage;
